import { hash01, leafAge, LEAF_COLORS, mix, treeSeason, vietnamMonthDay, yearFraction } from '../game/season';

/**
 * The leaf gift (AGENTS.md §7.4): after a full rest, one Bodhi leaf falls in front of the visitor.
 * It can be kept or sent as a card. Nothing is counted, collected or sent to the server.
 */

/** Today's leaf colour from the tree's year: yellowing in the dry season, pink-red at the flush, green otherwise. */
export function todayLeafColor(now: Date = new Date()): number {
  const { month, day } = vietnamMonthDay(now);
  const dayIndex = Math.round(yearFraction(month, day) * 365);
  return LEAF_COLORS[leafAge(treeSeason(yearFraction(month, day)), hash01(dayIndex, 7))];
}

/** The same heart-shaped leaf with a long drip tip as the garden's falling leaves, on a 48×72 grid, tip down. */
export function leafPath(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.moveTo(24, 12);
  ctx.bezierCurveTo(16, 2, 2, 4, 3, 20);
  ctx.bezierCurveTo(4, 38, 16, 50, 22, 58);
  ctx.lineTo(24, 71);
  ctx.lineTo(26, 58);
  ctx.bezierCurveTo(32, 50, 44, 38, 45, 20);
  ctx.bezierCurveTo(46, 4, 32, 2, 24, 12);
  ctx.closePath();
}

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

export type LeafCardText = { garden: string; line: string; date: string; lunar: string; domain: string };
export const CARD_SIZE = { width: 1080, height: 1350 } as const;

/** One line if it fits; otherwise two lines of even width, so no word is left alone below. */
export function balancedLines(text: string, maxWidth: number, measure: (line: string) => number): string[] {
  if (measure(text) <= maxWidth) return [text];
  const words = text.split(' ');
  let best = [text], widest = Infinity;
  for (let i = 1; i < words.length; i++) {
    const lines = [words.slice(0, i).join(' '), words.slice(i).join(' ')];
    const width = Math.max(...lines.map(measure));
    if (width < widest) { widest = width; best = lines; }
  }
  return best;
}

async function loadImage(src: string): Promise<HTMLImageElement | null> {
  try { const image = new Image(); image.src = src; await image.decode(); return image; } catch { return null; }
}

/** Draws the card in the browser: the chosen sky, today's leaf, the garden name, one short line and the date. */
export async function renderLeafCard(text: LeafCardText, color: number, background: string): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_SIZE.width; canvas.height = CARD_SIZE.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_unavailable');
  await Promise.all(['500 46px "Lora Variable"', 'italic 400 56px "Lora Variable"', '400 30px "Be Vietnam Pro"', '500 28px "Be Vietnam Pro"'].map(font => document.fonts?.load(font).catch(() => [])));
  const { width, height } = CARD_SIZE;
  ctx.fillStyle = '#eef1e6'; ctx.fillRect(0, 0, width, height);
  const image = await loadImage(background);
  if (image) {
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const w = image.naturalWidth * scale, h = image.naturalHeight * scale;
    ctx.drawImage(image, (width - w) / 2, (height - h) / 2 - 60, w, h);
  }
  const veil = ctx.createLinearGradient(0, 560, 0, 900);
  veil.addColorStop(0, 'rgba(246,245,238,0)'); veil.addColorStop(1, 'rgba(246,245,238,0.94)');
  ctx.fillStyle = veil; ctx.fillRect(0, 560, width, 340);
  ctx.fillStyle = 'rgba(246,245,238,0.94)'; ctx.fillRect(0, 900, width, height - 900);

  // The leaf, resting slightly turned, as if it has just fallen.
  ctx.save();
  ctx.translate(540, 470); ctx.rotate(-0.14); ctx.scale(6.4, 6.4); ctx.translate(-24, -38);
  ctx.shadowColor = 'rgba(36,48,30,0.28)'; ctx.shadowBlur = 5; ctx.shadowOffsetY = 2;
  leafPath(ctx); ctx.fillStyle = hex(color); ctx.fill();
  ctx.shadowColor = 'transparent';
  const shine = ctx.createLinearGradient(0, 0, 48, 72);
  shine.addColorStop(0, 'rgba(255,255,255,0.22)'); shine.addColorStop(0.6, 'rgba(255,255,255,0)');
  leafPath(ctx); ctx.fillStyle = shine; ctx.fill();
  ctx.strokeStyle = hex(mix(color, 0xffffff, 0.42)); ctx.lineCap = 'round';
  ctx.lineWidth = 0.7; ctx.beginPath(); ctx.moveTo(24, 10); ctx.lineTo(24, 66); ctx.stroke();
  ctx.lineWidth = 0.45;
  for (const y of [22, 30, 38, 46]) { ctx.beginPath(); ctx.moveTo(24, y); ctx.lineTo(11 + (y - 22) * 0.25, y - 8); ctx.moveTo(24, y); ctx.lineTo(37 - (y - 22) * 0.25, y - 8); ctx.stroke(); }
  ctx.restore();

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#3e5642'; ctx.font = '500 46px "Lora Variable", Georgia, serif';
  ctx.fillText(text.garden, 540, 905);
  ctx.fillStyle = '#2f4234'; ctx.font = 'italic 400 56px "Lora Variable", Georgia, serif';
  const lines = balancedLines(text.line, 920, line => ctx.measureText(line).width);
  lines.forEach((line, index) => ctx.fillText(line, 540, 990 + index * 70));
  const after = 990 + (lines.length - 1) * 70;
  ctx.fillStyle = '#5f7356'; ctx.font = '400 30px "Be Vietnam Pro", system-ui, sans-serif';
  ctx.fillText(text.date, 540, after + 82);
  ctx.fillText(text.lunar, 540, after + 128);
  ctx.fillStyle = '#6f8063'; ctx.font = '500 28px "Be Vietnam Pro", system-ui, sans-serif';
  ctx.fillText(text.domain, 552, 1272);
  ctx.save(); ctx.translate(540 - ctx.measureText(text.domain).width / 2 - 26, 1250); ctx.scale(0.42, 0.42); ctx.translate(-24, -38); leafPath(ctx); ctx.fillStyle = '#6f8063'; ctx.fill(); ctx.restore();

  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('card_failed')), 'image/png'));
}
